// Connect your agent (Features 4): the one step that gets a new person's agent onto the board, said the same in
// the checklist and in the chat. The board cannot see a computer's apps before the runner is linked, so before
// then it offers the one set-up line ("If you have Claude Code or Codex, this links it"); once a computer is
// linked, its runner says which of Claude Code and Codex it has and whether each is signed in, and the board says
// so ("Claude Code is on Studio-PC"), with Use it, one click.

import { useState } from "react";
import { Check, Copy } from "lucide-react";
import type { ApiKey, RunnerState } from "../../shared/types";
import type { Provider } from "../agent/config";
import { agentOf, runnerCommand, runnerShell, useRunnerCode, useRunnerOs, useRunnerRecheck, type AgentId } from "../agent/runnerChat";
import { RunnerOsSwitch } from "../agent/RunnerOs";
import { chat, openChat, switchAgent, useChat } from "../agent/store";
import { copyText } from "../lib/util";
import { set, useStore } from "../state/store";

export const APP: Record<AgentId, string> = { claude: "Claude Code", codex: "Codex" };
const PROVIDER: Record<AgentId, Provider> = { claude: "anthropic", codex: "openai" };

/** Where the user's agent stands, as the board can tell: what a linked computer has, or nothing linked yet. */
export type AgentLink =
  /** an agent ready on a linked computer: the chat runs on it */
  | { s: "ready"; agent: AgentId; host: string; also: AgentId | null }
  /** linked, and an agent there, but not signed in */
  | { s: "signin"; agent: AgentId; host: string; problem: string | null }
  /** linked, with neither Claude Code nor Codex on it */
  | { s: "none"; host: string }
  /** a computer set up before, off or asleep now */
  | { s: "off"; host: string }
  /** an API key for the chat in this browser */
  | { s: "key"; provider: Provider }
  /** an AI app connected over MCP (Claude, ChatGPT, Cursor) */
  | { s: "mcp" }
  /** nothing yet */
  | { s: "new" };

/** Where the agent stands. The agent the chat is set to comes first when a computer has both. */
export function agentLink(runner: RunnerState | null | undefined, keys: ApiKey[], provider: Provider, hasKey: boolean, outside: boolean): AgentLink {
  const want = agentOf(provider);
  if (runner?.online) {
    const host = runner.host ?? "your computer";
    const ready = runner.agents.filter((a) => a.ready).sort((a, b) => Number(b.id === want) - Number(a.id === want));
    if (ready.length) return { s: "ready", agent: ready[0].id, host, also: ready[1]?.id ?? null };
    const there = runner.agents.find((a) => a.id === want) ?? runner.agents[0];
    if (there) return { s: "signin", agent: there.id, host, problem: there.problem ?? null };
    return { s: "none", host };
  }
  if (hasKey) return { s: "key", provider };
  const paired = keys.find((k) => k.kind === "runner");
  if (paired) return { s: "off", host: paired.host ?? "Your computer" };
  if (outside) return { s: "mcp" };
  return { s: "new" };
}

/** Whether the agent counts as connected, for the checklist: the chat can run, or an AI app works on the board. */
export const agentConnected = (l: AgentLink) => l.s === "ready" || l.s === "key" || l.s === "mcp";

/** The link as this page knows it, kept current. */
export function useAgentLink(): AgentLink {
  const runner = useStore((s) => s.runner);
  const keys = useStore((s) => s.keys);
  const outside = useStore((s) => s.keys.some((k) => k.kind === "agent" && k.via !== "runner" && (!!k.lastUsedAt || k.via === "signin" || k.via === "setup")) || Object.values(s.agents).some((a) => !a.own));
  const provider = useChat((s) => s.config.provider);
  const hasKey = useChat((s) => !!s.config.key);
  return agentLink(runner, keys, provider, hasKey, outside);
}

/** Run the chat on this agent, and open it. */
export function runOn(agent: AgentId) {
  const p = PROVIDER[agent];
  if (chat.get().config.provider !== p) switchAgent(p);
  openChat();
}

function CopyLine({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <div className="ob-copy">
      <code>{text}</code>
      <button
        className="btn btn-sm"
        onClick={() =>
          void copyText(text).then((ok) => {
            setDone(ok);
            setTimeout(() => setDone(false), 1600);
          })
        }
      >
        {done ? <Check size={13} /> : <Copy size={13} />} {done ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

/** The set-up line: the one command that links a computer, and with it the Claude Code or Codex it has. */
function SetupLine() {
  const code = useRunnerCode(true);
  const [os, setOs] = useRunnerOs();
  return (
    <div className="ob-setup">
      <p className="ob-lead">If you have Claude Code or Codex, this links it.</p>
      <p>Run it once in {runnerShell(os)} on the computer where you use them. The chat then runs on your own plan, with no API key.</p>
      <RunnerOsSwitch os={os} onChange={setOs} />
      {code ? <CopyLine text={runnerCommand(code, os)} /> : <div className="ob-copy is-wait">Making your line…</div>}
      <p className="ob-waiting">
        <span className="spinner" /> Waiting for your computer
      </p>
    </div>
  );
}

/** While a linked computer has no agent ready, it looks again each minute (as the chat always did), so one installed or signed in shows up. */
function Recheck() {
  useRunnerRecheck(true);
  return null;
}

/** Other ways in, for someone with neither app: a key, or an AI app over MCP. */
function OtherWays({ provider }: { provider: Provider }) {
  return (
    <div className="ob-other">
      <button
        className="link-btn"
        onClick={() => {
          openChat();
          chat.set({ keyFor: provider });
        }}
      >
        Use an {provider === "openai" ? "OpenAI" : "Anthropic"} API key instead
      </button>
      <span aria-hidden>·</span>
      <button className="link-btn" onClick={() => set({ panel: "connect", panelArg: { tab: "ai" } })}>
        Use Claude, ChatGPT or Cursor
      </button>
    </div>
  );
}

/**
 * What to do next to connect the agent, for where it stands. Ready, it is the one-click line; otherwise the one step
 * that gets there. onUse runs after Use it.
 */
export function ConnectAgent({ link, onUse }: { link: AgentLink; onUse?: () => void }) {
  const provider = useChat((s) => s.config.provider);
  const [fresh, setFresh] = useState(false);
  if (link.s === "ready")
    return (
      <div className="ob-ready">
        <p>
          <b>{APP[link.agent]}</b> {link.also ? <>and <b>{APP[link.also]}</b> are</> : "is"} on {link.host}.
        </p>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => {
            runOn(link.agent);
            onUse?.();
          }}
        >
          Use it
        </button>
      </div>
    );
  if (link.s === "key")
    return (
      <div className="ob-ready">
        <p>The chat runs on your {link.provider === "openai" ? "OpenAI" : "Anthropic"} API key in this browser.</p>
      </div>
    );
  if (link.s === "mcp") return <p className="ob-note">An AI app is connected to your board.</p>;
  if (link.s === "signin")
    return (
      <>
        <p className="ob-note">
          {APP[link.agent]} is on {link.host}, but not signed in. Sign in to it there and it shows here within a minute.{link.problem ? ` ${link.problem}` : ""}
        </p>
        <Recheck />
        <OtherWays provider={provider} />
      </>
    );
  if (link.s === "none")
    return (
      <>
        <p className="ob-note">Claude Code and Codex aren't on {link.host} yet. Install one and sign in there, and it shows here within a minute.</p>
        <Recheck />
        <OtherWays provider={provider} />
      </>
    );
  if (link.s === "off" && !fresh)
    return (
      <>
        <p className="ob-note">{link.host} is linked, but it's off or asleep. Turn it on and the chat runs on it again.</p>
        <div className="ob-other">
          <button className="link-btn" onClick={() => setFresh(true)}>
            Link a different computer
          </button>
        </div>
      </>
    );
  return (
    <>
      <SetupLine />
      <OtherWays provider={provider} />
    </>
  );
}

/**
 * Show the one step that connects the agent, from anywhere (a Button pressed with no agent): the checklist opens on
 * it where there is one, else Connect opens on Your AI.
 */
export function showConnectAgent(): void {
  const shown = document.querySelector('[data-ob="checklist"], [data-ob="checklist-open"]');
  if (shown) window.dispatchEvent(new Event("cb:connect-agent"));
  else set({ panel: "connect", panelArg: { tab: "ai" } });
}
